import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Camera, X } from "lucide-react";
import { toast } from "sonner";

const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setIsCameraReady(true);
      }
    } catch (error) {
      console.error('Camera access error:', error);
      toast.error('Unable to access camera. Please check permissions.');
      onClose();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            onCapture(reader.result);
            stopCamera();
            onClose();
          };
          reader.readAsDataURL(blob);
        }
      }, 'image/jpeg', 0.85);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.95)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{ maxWidth: '800px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 600 }}>Take Photo</h2>
          <Button variant="ghost" onClick={() => { stopCamera(); onClose(); }} style={{ color: 'white' }}>
            <X className="w-6 h-6" />
          </Button>
        </div>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            maxHeight: '70vh',
            borderRadius: '12px',
            background: '#000'
          }}
          data-testid="camera-video"
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {isCameraReady && (
          <Button
            onClick={capturePhoto}
            size="lg"
            style={{
              width: '100%',
              marginTop: '1rem',
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              fontSize: '1.1rem',
              padding: '1.5rem'
            }}
            data-testid="capture-photo-button"
          >
            <Camera className="w-5 h-5 mr-2" />
            Capture Photo
          </Button>
        )}
      </div>
    </div>
  );
};

export default CameraCapture;